/**
 * CS: Campaign Tier Hiding
 *
 * Detects SMS / email-campaign visitors and sets a Shopify cart attribute
 * (`cs_campaign_channel`) so Liquid can hide the 20%/30% discount tiers
 * in the cart drawer progress bar.
 *
 * Detection rules (checked in order):
 *   1. UTM params:  utm_source=postscript OR utm_medium=sms  → SMS
 *                   utm_source=email-campaign                → Email
 *   2. Klaviyo _kx: _kx param present (Safari ITP strips UTMs but keeps _kx) → Email
 *   3. Referrer:    pscrpt.io domain → SMS (catches stripped-UTM redirects)
 *
 * Session behavior:
 *   - First page in session (no sessionStorage key): detect & decide.
 *   - Subsequent pages: respect prior decision (do nothing unless new UTMs).
 *   - New session (tab closed / next day): re-evaluate from scratch.
 *
 * Clearing: On first visit with no campaign signal, any stale
 * `cs_campaign_channel` attribute from a previous Shopify session is
 * cleared so returning direct visitors see the full tiered progress bar.
 */
(function () {
  const KEY = 'cs_campaign_set';
  const existing = sessionStorage.getItem(KEY);

  const params = new URLSearchParams(window.location.search);
  const src = (params.get('utm_source') || '').toLowerCase();
  const med = (params.get('utm_medium') || '').toLowerCase();

  let channel = '';
  if (src === 'postscript' || med === 'sms') {
    channel = 'sms';
  } else if (src === 'email-campaign') {
    channel = 'email';
  }

  /* Klaviyo _kx fallback: Safari ITP strips utm_* params during
     Klaviyo click-tracking redirects, but _kx survives. */
  if (!channel && params.has('_kx')) {
    channel = 'email';
  }

  /* Postscript redirect fallback: pscrpt.io links strip UTMs,
     but the referrer survives. Only check on first visit. */
  if (!channel && existing === null) {
    try {
      const ref = new URL(document.referrer);
      if (ref.hostname.indexOf('pscrpt.io') !== -1) {
        channel = 'sms';
      }
    } catch {}
  }

  if (channel) {
    /* Campaign signal detected — set or update */
    if (existing === channel) return;
    setAttr(channel);
  } else if (existing === null) {
    /* First visit in session, no campaign signal — clear stale attribute */
    clearStaleAttr();
  }
  /* Subsequent visits without UTMs: respect prior determination (do nothing) */

  async function setAttr(ch) {
    sessionStorage.setItem(KEY, ch);
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { cs_campaign_channel: ch } }),
      });
      document.dispatchEvent(new CustomEvent('discount:update'));
    } catch {}
  }

  async function clearStaleAttr() {
    sessionStorage.setItem(KEY, 'direct');
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      if (cart.attributes && cart.attributes.cs_campaign_channel) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { cs_campaign_channel: '' } }),
        });
        document.dispatchEvent(new CustomEvent('discount:update'));
      }
    } catch {}
  }
})();
